'use client';

import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Upload, Download, Copy, Trash2, ArrowRight, ArrowDown, Code, Settings, Check, X } from 'lucide-react';
import { saveAs } from 'file-saver';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { luaBeautifierContent } from '../../data/lua-beautifier-content';
import { cn } from '@/lib/utils';

// Simple Lua formatter since lua-fmt has issues
const formatLuaCode = (luaCode: string, options: FormatOptions): { formatted: string; error?: string } => {
    try {
        if (!luaCode.trim()) return { formatted: '', error: 'Empty Lua content' };

        // Basic formatting - this is a simplified version
        let formatted = luaCode;
        const indent = options.useTabs ? '\t' : ' '.repeat(options.indentCount);
        const quotemark = options.quotemark === 'single' ? '\'' : '"';

        // Basic indentation and line breaks
        let level = 0;
        const lines = luaCode.split('\n');
        const formattedLines: string[] = [];

        for (let line of lines) {
            line = line.trim();
            if (!line) {
                formattedLines.push('');
                continue;
            }

            // Handle indentation
            if (line.includes('end') || line.includes('else') || line.includes('elseif') || line.includes('until')) {
                level = Math.max(0, level - 1);
            }

            // Add indentation
            formattedLines.push(indent.repeat(level) + line);

            // Increase level for blocks
            if (line.endsWith('then') || line.endsWith('do') || line.startsWith('function') ||
                line.endsWith('{') || (line.includes('=') && line.includes('{') && !line.includes('}')) ||
                line.endsWith('(') || (line.includes('(') && !line.includes(')') && !line.endsWith(')'))
            ) {
                level++;
            }
        }

        // Join lines with proper line breaks
        formatted = formattedLines.join('\n');

        // Basic quote normalization
        if (options.quotemark === 'single') {
            formatted = formatted.replace(/(?<![\\'])"([^"]*?)(?<![\\'])"/g, `'$1'`);
        } else {
            formatted = formatted.replace(/(?<![\\'])'([^']*?)(?<![\\])(?<!')'/g, '"$1"');
        }

        return { formatted };
    } catch (error) {
        console.error('Error formatting Lua code:', error);
        return {
            formatted: luaCode,
            error: error instanceof Error ? error.message : 'An error occurred while formatting Lua code'
        };
    }
};

interface CodeEditorProps {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
    hasError?: boolean;
}

const CodeEditor = ({
    value,
    onChange,
    readOnly,
    placeholder,
    className,
    hasError = false
}: CodeEditorProps) => {
    return (
        <div className={cn(
            "relative flex h-full w-full overflow-hidden rounded-md border bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            hasError ? 'border-destructive' : '',
            className
        )}>
            <Textarea
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                readOnly={readOnly}
                className="h-full w-full resize-none border-0 focus-visible:ring-0 rounded-none p-4 font-mono text-sm leading-6"
                placeholder={placeholder}
                spellCheck={false}
            />
            {hasError && (
                <div className="absolute top-2 right-2 p-1 bg-destructive/10 rounded-full">
                    <X className="h-4 w-4 text-destructive" />
                </div>
            )}
        </div>
    );
};

interface FormatOptions {
    indentCount: number;
    lineWidth: number;
    useTabs: boolean;
    quotemark: 'single' | 'double';
    alignParameters: boolean;
    alignCompound: boolean;
    alignTableField: boolean;
    breakAfterOperator: boolean;
    chopDownTable: boolean;
}

const defaultOptions: FormatOptions = {
    indentCount: 4,
    lineWidth: 80,
    useTabs: false,
    quotemark: 'single',
    alignParameters: true,
    alignCompound: true,
    alignTableField: true,
    breakAfterOperator: false,
    chopDownTable: false,
};

// Format options interface is already defined above

export default function LuaBeautifier() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [options, setOptions] = useState<FormatOptions>(defaultOptions);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    const formatLuaHandler = () => {
        try {
            if (!input.trim()) {
                toast({
                    title: "Error",
                    description: "Please enter some Lua code to format",
                    variant: "destructive",
                });
                return;
            }

            const { formatted, error } = formatLuaCode(input, options);

            if (error) {
                throw new Error(error);
            }

            setOutput(formatted);
            setIsValid(true);

            toast({
                title: "Success",
                description: "Lua code formatted successfully!",
            });
        } catch (error) {
            setIsValid(false);
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "An error occurred while formatting Lua code",
                variant: "destructive",
            });
            console.error('Error formatting Lua code:', error);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
            setIsValid(true);
            toast({
                title: "File Uploaded",
                description: `Loaded ${file.name}`,
            });
        };
        reader.readAsText(file);
    };

    const handleDownload = () => {
        if (!output) return;
        const blob = new Blob([output], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, 'formatted.lua');
        toast({
            title: "Downloaded",
            description: "File saved as formatted.lua",
        });
    };

    const copyToClipboard = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        toast({
            title: "Copied!",
            description: "Formatted Lua code copied to clipboard",
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setIsValid(true);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const sampleLua = `-- Sample Lua code
local function factorial(n)
    if n == 0 then
        return 1
    else
        return n * factorial(n - 1)
    end
end

local function fibonacci(n)
    local a, b = 0, 1
    for i = 1, n do
        a, b = b, a + b
    end
    return a
end

local config = {
    name = "Lua Formatter",
    version = "1.0.0",
    author = "Your Name",
    settings = {
        indent = 4,
        lineWidth = 80,
        useTabs = false,
        quotemark = 'single',
        alignParameters = true,
        alignCompound = true
    }
}

print("Factorial of 5:", factorial(5))
print("10th Fibonacci number:", fibonacci(10))`;

    const loadSample = () => {
        setInput(sampleLua);
        setIsValid(true);
    };

    return (
        <div className="container mx-auto p-4 max-w-[1400px]">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <ToolPageIntro title="Lua Code Beautifier">
                Format and beautify Lua code with proper indentation and syntax highlighting. Free online Lua code formatter.
            </ToolPageIntro>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                        className="gap-2"
                    >
                        <Settings className="h-4 w-4" />
                        {isOptionsOpen ? 'Hide Options' : 'Format Options'}
                    </Button>
                </div>
            </div>

            {/* Options Panel */}
            {isOptionsOpen && (
                <Card className="mb-6">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Formatting Options</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Indentation</label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.indentCount}
                                onChange={(e) => setOptions({ ...options, indentCount: parseInt(e.target.value) || 4 })}
                            >
                                <option value="2">2 spaces</option>
                                <option value="4">4 spaces</option>
                                <option value="8">8 spaces</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Line Width</label>
                            <input
                                type="number"
                                min="20"
                                max="200"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.lineWidth}
                                onChange={(e) => setOptions({ ...options, lineWidth: parseInt(e.target.value) || 80 })}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Quote Style</label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.quotemark}
                                onChange={(e) => setOptions({ ...options, quotemark: e.target.value as 'single' | 'double' })}
                            >
                                <option value="single">Single quotes (')</option>
                                <option value="double">Double quotes (")</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none flex items-center">
                                <input
                                    type="checkbox"
                                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={options.useTabs}
                                    onChange={(e) => setOptions({ ...options, useTabs: e.target.checked })}
                                />
                                Use Tabs Instead of Spaces
                            </label>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none flex items-center">
                                <input
                                    type="checkbox"
                                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={options.alignParameters}
                                    onChange={(e) => setOptions({ ...options, alignParameters: e.target.checked })}
                                />
                                Align Parameters
                            </label>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none flex items-center">
                                <input
                                    type="checkbox"
                                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={options.alignCompound}
                                    onChange={(e) => setOptions({ ...options, alignCompound: e.target.checked })}
                                />
                                Align Compound Statements
                            </label>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-250px)]">
                {/* Input Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-full">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <CardTitle className="flex justify-between items-center">
                            <span>Input Lua Code</span>
                            <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={loadSample} className="text-muted-foreground">
                                    Sample
                                </Button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept=".lua,.txt"
                                    className="hidden"
                                    id="file-upload"
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-muted-foreground"
                                >
                                    <Upload className="h-4 w-4 mr-1" /> Upload
                                </Button>
                                <Button variant="ghost" size="sm" onClick={clearAll} className="text-muted-foreground hover:text-destructive">
                                    <Trash2 className="h-4 w-4" /> Clear
                                </Button>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 relative overflow-hidden">
                        <CodeEditor
                            value={input}
                            onChange={setInput}
                            placeholder="Paste your Lua code here, upload a file, or try the sample..."
                            className="border-0 rounded-none"
                            hasError={!isValid && input.trim() !== ''}
                        />
                    </CardContent>
                </Card>

                {/* Action Section */}
                <div className="flex lg:flex-col justify-center items-center gap-4 py-4 lg:py-0">
                    <Button
                        onClick={formatLuaHandler}
                        size="lg"
                        className="rounded-full w-16 h-16 lg:w-20 lg:h-20 p-0 shadow-lg hover:scale-105 transition-transform"
                        title="Format Lua Code"
                    >
                        <ArrowRight className="h-8 w-8 hidden lg:block" />
                        <ArrowDown className="h-8 w-8 lg:hidden" />
                    </Button>
                </div>

                {/* Output Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-full">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <CardTitle className="text-lg font-medium">Formatted Output</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={copyToClipboard} disabled={!output} title="Copy">
                                    <Copy className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon" onClick={handleDownload} disabled={!output} title="Download">
                                    <Download className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 bg-muted/30 relative overflow-hidden">
                        <CodeEditor
                            value={output}
                            readOnly
                            placeholder="Formatted Lua code will appear here..."
                            className="border-0 rounded-none bg-transparent"
                        />
                    </CardContent>
                </Card>
            </div>
            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={luaBeautifierContent.title}
                    description={luaBeautifierContent.description}
                    content={luaBeautifierContent.content}
                    faqs={luaBeautifierContent.faqs}
                    relatedTools={luaBeautifierContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
